import cn from 'classnames';
import * as React from 'react';
import {Paper} from '@material-ui/core';
import VideocamIcon from '@material-ui/icons/Videocam';
import {ImageProps} from 'material-ui-image/lib/components/Image/Image';

import {RendererProps} from '../Renderer';
import {StudioTooltip} from '../../StudioTooltip/StudioTooltip';

import './VideoItem.scss';

type VideoItemProps = {
  name?: string;
  className?: string;
  selectable?: boolean;
  adornment?: React.ReactNode;
  url?: string | undefined | null | File;
  onClick?: ImageProps['onClick'];
} & Pick<RendererProps, 'isBusy' | 'isLoadingThumbnail'>;

export const VideoItem = ({
  url,
  name,
  isBusy,
  adornment,
  className,
  selectable = false,
  isLoadingThumbnail = false,
  onClick,
}: VideoItemProps) => {
  const thumbnail = React.useMemo(() => {
    let thumbnailUrl = null;

    if (url) {
      thumbnailUrl = typeof url === 'string' ? url : URL.createObjectURL(url);
    }

    return thumbnailUrl;
  }, [url]);

  const [videoName, videoExtension, videoFullName] = React.useMemo(() => {
    let videoName = '';
    let videoFullName = '';
    let videoExtension = '';

    if (name) {
      videoFullName = name.split('/').pop() || '';
      videoName = videoFullName;
    } else if (url) {
      if (typeof url === 'string') {
        const urlParts = url.split('/');

        videoName = urlParts[urlParts.length - 1];
      } else if (url instanceof File) {
        videoName = url.name;
      }
    }

    const lastDotIndex = videoName.lastIndexOf('.');

    if (lastDotIndex !== -1) {
      videoExtension = videoName.substring(lastDotIndex);
      videoName = videoName.substring(0, lastDotIndex);
    }

    return [videoName, videoExtension, videoFullName];
  }, [name, url]);

  return (
    <StudioTooltip title={videoFullName} placement="top">
      <Paper
        className={cn('fileviewer__video-item', className, {
          'fileviewer__video-item_selectable': selectable,
        })}
        elevation={0}
        onClick={onClick}
      >
        <div className="fileviewer__video-item-content-container">
          {!isLoadingThumbnail && thumbnail ? (
            <img
              src={thumbnail}
              className={cn('fileviewer__video-item-thumbnail', {
                'fileviewer__video-item-thumbnail--busy': isBusy,
              })}
              alt={videoFullName}
            />
          ) : (
            <div
              className={cn('fileviewer__video-item-placeholder', {
                'fileviewer__video-item-thumbnail--busy': isLoadingThumbnail,
              })}
            >
              <VideocamIcon color="primary" className="fileviewer__video-item-icon" />
            </div>
          )}
        </div>
        <div className="fileviewer__video-item-name">
          <span className="fileviewer__video-item-name-text">{videoName}</span>
          <span className="fileviewer__video-item-name-extension">{videoExtension}</span>
        </div>
        {adornment}
      </Paper>
    </StudioTooltip>
  );
};
